/**
 * Publish-side configuration: the exact simulcast ladder from
 * docs/CAPACITY-EX44.md §1.2 and the AV1 → VP9 → VP8 preference from
 * `CODEC_PREFERENCE`, resolved by real browser capability detection.
 *
 * AV1/VP9 are SVC codecs: LiveKit replaces simulcast with a single SVC stream
 * (`L3T3_KEY` = three spatial rungs, same shape as the ladder) and publishes a
 * VP8 backup for clients that cannot decode it. That is where the study's
 * "30–40 % cheaper than these VP8-calibrated numbers" headroom comes from.
 */
import {
  AUDIO_SETTINGS, CODEC_PREFERENCE, LAYER_SPECS, SIMULCAST_LADDER,
  type MediaLayer, type QualityProfile,
} from '@cyphex/types';
import {
  VideoPreset, isVideoCodec,
  supportsAV1, supportsVP9,
  type ScalabilityMode, type TrackPublishDefaults, type VideoCodec, type VideoEncoding,
} from 'livekit-client';
import type { ScreenShareHint } from './contract';
import { mediaLog } from './mediaLog';
import { cameraPublishBitrate, shareBitrate, shareFps } from './shareBudget';

/** Codecs LiveKit drives with SVC instead of simulcast. */
const SVC_CODECS: readonly VideoCodec[] = ['av1', 'vp9'];
const SVC_MODE: ScalabilityMode = 'L3T3_KEY';
const TOP_LADDER_LAYER: MediaLayer = 'l3_720p';
const BACKUP_LAYER: MediaLayer = 'l1_360p';

const CAN_ENCODE: Readonly<Record<VideoCodec, () => boolean>> = {
  av1: supportsAV1,
  vp9: supportsVP9,
  vp8: () => true,
  h264: () => true,
  h265: () => false,
};

/** Best codec this browser can actually encode, in the normative order. */
export function preferredVideoCodec(): VideoCodec {
  for (const candidate of CODEC_PREFERENCE) {
    if (!isVideoCodec(candidate)) continue;
    if (CAN_ENCODE[candidate]()) return candidate;
  }
  return 'vp8';
}

export const isSvcCodec = (codec: VideoCodec): boolean => SVC_CODECS.includes(codec);

/**
 * Codec for the screen share: the best NON-SVC codec this browser can encode.
 *
 * WHY not `preferredVideoCodec()`. When the publish codec is AV1 or VP9,
 * `LocalParticipant.publishTrack` forces `scalabilityMode: 'L1T3'` for a
 * ScreenShare source AND overwrites the track's `contentHint` to `'motion'`
 * (livekit-client 2.21.0, working around a Chrome bug that caps L1T3 screen
 * content at 5 fps). That silently cancels the `'detail'` hint `localMedia`
 * sets on the track, so Chrome encodes a desktop as if it were camera video:
 * motion-tuned rate control, temporal denoising, and no screen-content coding
 * mode. At the ~1.5 Mbps share budget that is what turns text to mush. Staying
 * off the SVC branch is the only way to keep the hint we asked for.
 *
 * This costs nothing against the design point: CAPACITY-EX44 §1.2 calibrates
 * every share figure on VP8 and treats AV1/VP9 efficiency as headroom that is
 * explicitly "not spent in advance". Cameras keep `preferredVideoCodec()`.
 */
export function screenShareCodec(): VideoCodec {
  for (const candidate of CODEC_PREFERENCE) {
    if (!isVideoCodec(candidate)) continue;
    if (isSvcCodec(candidate)) continue;
    if (CAN_ENCODE[candidate]()) return candidate;
  }
  return 'vp8';
}

export const encodingFor = (layer: MediaLayer): VideoEncoding => ({
  maxBitrate: LAYER_SPECS[layer].bitrate,
  maxFramerate: LAYER_SPECS[layer].fps,
});

export const presetFor = (layer: MediaLayer): VideoPreset =>
  new VideoPreset({
    width: LAYER_SPECS[layer].width,
    height: LAYER_SPECS[layer].height,
    maxBitrate: LAYER_SPECS[layer].bitrate,
    maxFramerate: LAYER_SPECS[layer].fps,
  });

/**
 * Screen-share encoder settings. `contentHint` decides fps AND bitrate, and
 * `cameraBitrate` decides how much of that hint the publish ceiling can afford.
 *
 * The numbers live in `shareBudget`, which `localMedia` reads as well: the
 * capture request and the encoder ceiling are one decision, and keeping them in
 * one function is the only way they cannot disagree.
 */
export function screenShareEncoding(
  profile: QualityProfile,
  hint: ScreenShareHint,
  cameraBitrate: number,
  /** `SHARE_AUDIO_BITRATE` when this share carries sound; 0 when it does not. */
  shareAudioBitrate = 0,
): VideoEncoding {
  return {
    maxBitrate: shareBitrate(profile, hint, cameraBitrate, shareAudioBitrate),
    maxFramerate: shareFps(profile, hint),
  };
}

/**
 * Says out loud when a share did NOT land on the codec we asked for.
 *
 * `publishTrack` does not fail when it cannot honour `videoCodec`. If the SFU
 * advertises `enabledPublishVideoCodecs` and our choice is absent from it,
 * livekit-client silently substitutes `enabledPublishVideoCodecs[0]`; a second
 * fallback later adopts whatever codec the server named in the AddTrack
 * response. Either one can put the share back on AV1/VP9 — and then the
 * `contentHint` overwrite that `screenShareCodec` exists to dodge returns, the
 * share goes soft, and NOTHING says so: no exception, no failed publish, no
 * symptom a presenter can name. The dependency is on infra/livekit/livekit.yaml,
 * a file this client cannot read and must not change, so the least it can do is
 * notice out loud when that file has moved underneath it.
 *
 * Never throws. A soft share still shows the deck; no share shows nothing.
 */
export function verifyShareCodec(
  negotiated: VideoCodec | undefined,
  requested: VideoCodec,
): void {
  if (negotiated === undefined || negotiated === requested) return;
  if (isSvcCodec(negotiated)) {
    mediaLog.reportOnce(
      'share-codec-svc',
      `Screen share published on ${negotiated}, not ${requested}. LiveKit forces contentHint to "motion" for SVC screen shares, so shared text will be soft. Check enabledPublishVideoCodecs on the SFU — ${requested} has left the list.`,
    );
    return;
  }
  /* Still non-SVC, so the `detail` hint survives and the share is fine. Worth
     knowing while working — the codec we reasoned about is not the codec on the
     wire — and not worth a line in a production console. */
  mediaLog.warn(`Screen share published on ${negotiated}, not ${requested}.`);
}

/** Room-wide publish defaults. Dynacast pauses whatever nobody is watching. */
export function publishDefaults(profile: QualityProfile): TrackPublishDefaults {
  const codec = preferredVideoCodec();
  const svc = isSvcCodec(codec);
  const lower = SIMULCAST_LADDER.filter((layer) => layer !== TOP_LADDER_LAYER);

  return {
    videoCodec: codec,
    simulcast: !svc,
    scalabilityMode: svc ? SVC_MODE : undefined,
    backupCodec: svc ? { codec: 'vp8', encoding: encodingFor(BACKUP_LAYER) } : true,
    videoEncoding: encodingFor(TOP_LADDER_LAYER),
    videoSimulcastLayers: lower.map(presetFor),
    /* Room-wide fallback only: `livekitPublisher` re-derives this per share
       with the camera's real state. Budgeting a live camera here is the
       conservative reading and costs nothing — `detail` fits inside the ceiling
       even then, so this value is what it always was. */
    screenShareEncoding: screenShareEncoding(profile, 'detail', cameraPublishBitrate()),
    degradationPreference: 'maintain-framerate',
    audioPreset: { maxBitrate: AUDIO_SETTINGS.maxBitrate },
    dtx: AUDIO_SETTINGS.dtx,
    red: AUDIO_SETTINGS.red,
    forceStereo: false,
    // localMedia owns the capture lifecycle; LiveKit must not stop our tracks.
    stopMicTrackOnMute: false,
  };
}
